import { openDB, type IDBPDatabase } from 'idb';
import type { ChildProfile, GameId, ProfileProgress } from '../types';

const DB_NAME = 'nagakeciks-kids';
const FALLBACK_KEY = 'nk-progress-fallback';
let dbPromise: Promise<IDBPDatabase> | undefined;

function getDb() {
  if (!dbPromise) dbPromise = openDB(DB_NAME, 1, { upgrade(db) { db.createObjectStore('profiles', { keyPath: 'id' }); db.createObjectStore('progress', { keyPath: 'profileId' }); } });
  return dbPromise;
}

const defaultProfile: ChildProfile = { id: 'little-explorer', name: 'Explorer', avatar: 'dragon', createdAt: new Date(0).toISOString() };
const emptyProgress = (profileId: string): ProfileProgress => ({ version: 2, profileId, games: {}, achievements: [], totalStars: 0, awardedSessionIds: [] });

type StoredProgress = Omit<ProfileProgress, 'version' | 'totalStars' | 'awardedSessionIds'> & {
  version?: 1 | 2;
  totalStars?: number;
  awardedSessionIds?: string[];
};

function normalizeProgress(progress: StoredProgress | undefined, profileId: string): ProfileProgress {
  if (!progress) return emptyProgress(profileId);
  const historicalStars = Object.values(progress.games ?? {}).reduce((sum, game) => sum + (game?.stars ?? 0), 0);
  return { ...progress, version: 2, profileId, games: progress.games ?? {}, achievements: progress.achievements ?? [], totalStars: progress.totalStars ?? historicalStars, awardedSessionIds: progress.awardedSessionIds ?? [] };
}

function readFallback(): { profiles: ChildProfile[]; progress: Record<string, ProfileProgress> } {
  try { return JSON.parse(localStorage.getItem(FALLBACK_KEY) ?? '') as ReturnType<typeof readFallback>; }
  catch { return { profiles: [defaultProfile], progress: {} }; }
}
function writeFallback(data: ReturnType<typeof readFallback>) { try { localStorage.setItem(FALLBACK_KEY, JSON.stringify(data)); } catch { /* private browsing fallback */ } }

export async function listProfiles(): Promise<ChildProfile[]> {
  try { const db = await getDb(); const profiles = await db.getAll('profiles') as ChildProfile[]; if (!profiles.length) { await db.put('profiles', defaultProfile); return [defaultProfile]; } return profiles; }
  catch { return readFallback().profiles; }
}

export async function addProfile(name: string, avatar = 'dragon'): Promise<ChildProfile> {
  const profile: ChildProfile = { id: crypto.randomUUID(), name: name.trim().slice(0, 16) || 'Explorer', avatar, createdAt: new Date().toISOString() };
  try { await (await getDb()).put('profiles', profile); }
  catch { const data = readFallback(); data.profiles.push(profile); writeFallback(data); }
  return profile;
}

export async function getProgress(profileId: string): Promise<ProfileProgress> {
  try { return normalizeProgress(await (await getDb()).get('progress', profileId) as StoredProgress | undefined, profileId); }
  catch { return normalizeProgress(readFallback().progress[profileId], profileId); }
}

export function calculateReward(stars: number) { return Math.max(0, Math.floor(stars)); }

function applyCompletion(progress: ProfileProgress, gameId: GameId, stars: number, sessionId: string) {
  if (progress.awardedSessionIds.includes(sessionId)) return progress;
  const reward = calculateReward(stars); const previous = progress.games[gameId]; const bestStars = Math.max(previous?.bestStars ?? 0, reward);
  progress.games[gameId] = { gameId, completions: (previous?.completions ?? 0) + 1, stars: (previous?.stars ?? 0) + reward, bestStars, lastPlayedAt: new Date().toISOString() };
  progress.totalStars += reward; progress.awardedSessionIds.push(sessionId);
  const completed = Object.keys(progress.games).length; const earnedStars = Object.values(progress.games).reduce((sum, game) => sum + (game?.stars ?? 0), 0);
  if (completed >= 1 && !progress.achievements.includes('first-adventure')) progress.achievements.push('first-adventure');
  if (completed >= 6 && !progress.achievements.includes('rainbow-explorer')) progress.achievements.push('rainbow-explorer');
  if (completed >= 10 && !progress.achievements.includes('world-explorer')) progress.achievements.push('world-explorer');
  if (earnedStars >= 12 && !progress.achievements.includes('star-friend')) progress.achievements.push('star-friend');
  return progress;
}

export async function recordCompletion(profileId: string, gameId: GameId, stars: number, sessionId: string): Promise<ProfileProgress> {
  if (!sessionId) throw new Error('A stable session ID is required to record a completion.');
  try {
    const db = await getDb(); const transaction = db.transaction('progress', 'readwrite'); const store = transaction.objectStore('progress');
    const progress = normalizeProgress(await store.get(profileId) as StoredProgress | undefined, profileId);
    applyCompletion(progress, gameId, stars, sessionId); await store.put(progress); await transaction.done; return progress;
  } catch {
    const data = readFallback(); const progress = normalizeProgress(data.progress[profileId], profileId); applyCompletion(progress, gameId, stars, sessionId); data.progress[profileId] = progress; writeFallback(data); return progress;
  }
}

export async function resetProgress(profileId: string) {
  try { await (await getDb()).delete('progress', profileId); }
  catch { const data = readFallback(); delete data.progress[profileId]; writeFallback(data); }
}

export function __resetDbForTests() { dbPromise = undefined; }
